import { gql, useQuery } from '@apollo/client';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconCommunity from '@apollo/icons/large/IconCommunity.svg';
import IconDocument from '@apollo/icons/large/IconDocument.svg';
import IconEducation from '@apollo/icons/large/IconEducation.svg';
import {
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
} from '@apollo/orbit';
import { debounce } from 'lodash';
import { parse } from 'query-string';
import React, { useCallback, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { graphOnboarding } from 'src/app/account/graphOnboarding/routes';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { BuildErrorModal } from 'src/app/graph/variantLayout/buildErrorModal/BuildErrorModal';
import { BillingManagerNoAccess } from 'src/components/billingManagerNoAccess/BillingManagerNoAccess';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useQueryParam } from 'src/hooks/useQueryParam';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CommunityRouter, DocsRouter, OdysseyRouter } from 'src/lib/routers';
import { mergeQueryParams, setOverlayClosedLocation } from 'src/lib/routing';

import * as orgRoutes from '../routes';

import { EmptyGraphLayout } from './emptyGraphLayout/EmptyGraphLayout';
import { GraphCreationButton } from './graphCreationButton/GraphCreationButton';
import { GraphErrorDisplay } from './GraphErrorDisplay';
import { InitiatingNewSupergraphEndpointModal } from './initiatingNewSupergraphEndpointModal/InitiatingNewSupergraphEndpointModal';
import { MigrationAlertBanner } from './routerMigrationBanner/MigrationAlert';
import { SupergraphsList } from './SupergraphsList';

const SUPERGRAPH_LIST_PAGE_QUERY = gql<
  GraphQLTypes.SupergraphsListPageQuery,
  GraphQLTypes.SupergraphsListPageQueryVariables
>`
  query SupergraphsListPageQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      capabilities {
        connectorsInRouter
      }
      services {
        id
        graphType
      }
      currentPlan {
        id
        kind
        tier
        name
      }
    }

    me {
      id
      ... on User {
        memberships {
          role: permission
          account {
            id
          }
          user {
            id
          }
        }
      }
    }
  }
`;

export const SupergraphsListPage = () => {
  const location = useLocation();
  const history = useHistory();
  const {
    orgId: accountId,
    initiatingSupergraphState,
    [Config.queryParameters.GraphId]: selectedGraphId,
    [Config.queryParameters.SelectedVariant]: selectedGraphVariant,
  } = useRouteParams(orgRoutes.graphs);

  const { data, error, loading } = useQuery(SUPERGRAPH_LIST_PAGE_QUERY, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    variables: {
      accountId,
    },
    skip: !accountId,
  });

  const initiatingGraphRef = initiatingSupergraphState
    ? {
        graphId: initiatingSupergraphState.graphId,
        graphVariant: initiatingSupergraphState.graphVariant,
      }
    : undefined;

  const membership =
    data?.me?.__typename === 'User'
      ? data.me.memberships.find(({ account }) => account.id === accountId)
      : undefined;
  const accountRole =
    membership?.__typename === 'UserMembership' ? membership.role : undefined;
  const isBillingManager =
    accountRole === GraphQLTypes.UserPermission.BILLING_MANAGER;
  const hasConnectorsCapabilities =
    !!data?.account?.capabilities?.connectorsInRouter;
  const currentPlan = data?.account?.currentPlan;
  const overlayName = useQueryParam(Config.queryParameters.Overlay);
  const { isDedicatedPlan, isServerlessPlan } = useCheckPlanTypes({
    tier: currentPlan?.tier,
    kind: currentPlan?.kind,
  });
  const isCloudOnlyPlan = isDedicatedPlan || isServerlessPlan;
  const hasGraphs = !!data?.account?.services?.length;
  const shouldSeeSupergraphAccountView =
    isCloudOnlyPlan &&
    !!data?.account?.services.some(
      ({ graphType }) => graphType === GraphQLTypes.GraphType.CLASSIC,
    );

  const [supergraphListSearch, setSupergraphListSearch] = useState<string>();

  const debouncedSearch = debounce((update) => {
    setSupergraphListSearch(update);
  }, 300);

  const closeInitiatingNewSupergraphModal = useCallback(
    (graphRef: GraphRef) => {
      history.replace({
        ...setOverlayClosedLocation({
          ...history.location,
        }),
        state: {
          ...(typeof history.location.state === 'object'
            ? history.location.state
            : {}),
          highlightSupergraphState: {
            graphId: graphRef.graphId,
          },
          initiatingSupergraphState: undefined,
        },
      });
    },
    [history],
  );

  if (loading) return <Loading />;

  if (error) return <GraphErrorDisplay error={error} />;

  if (isBillingManager) return <BillingManagerNoAccess accountId={accountId} />;

  if (!hasGraphs) {
    // If the user should see the "Supergraph Account View"
    if (isCloudOnlyPlan) {
      // If the user is a dedicated customer, redirect them to the supergraph onboarding path
      if (isDedicatedPlan) {
        const toLocation = orgRoutes.supergraphOnboarding.location({
          orgId: accountId,
        });
        return (
          <Redirect
            to={
              overlayName
                ? {
                    ...toLocation,
                    search: mergeQueryParams(toLocation.search, {
                      [Config.queryParameters.Overlay]: overlayName,
                    }),
                  }
                : toLocation
            }
            push={false}
          />
        );
      }

      // If serverless, redirect them to the graphOnboarding location
      return (
        <Redirect
          to={graphOnboarding.location({
            ...parse(location.search),
            orgId: accountId,
          })}
          push={false}
        />
      );
    }

    return (
      <EmptyGraphLayout
        accountId={accountId}
        canHaveConnectors={hasConnectorsCapabilities}
        planName={currentPlan?.name}
      />
    );
  }

  return (
    <>
      <div className="mx-8 mb-8 mt-4 grid h-full w-4/5 grid-cols-1 gap-6 self-center md:w-auto md:max-w-screen-2xl md:grid-cols-4">
        {/* Search/Creation and SupergraphsList sections - full width on small screens, 3/4 width on md+ */}
        <div className="row-span-2 flex flex-col md:col-span-3">
          <MigrationAlertBanner accountId={accountId} />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <FormControl className="min-w-[240px] flex-1">
              <FormLabel className="sr-only">Search graph list</FormLabel>
              <Input
                spellCheck={false}
                className="w-full"
                placeholder="Search graphs"
                onChange={(e) => {
                  debouncedSearch(e.target.value);
                }}
                leftElement={<IconSearch />}
              />
            </FormControl>
            <div className="max-w-full flex-shrink-0 whitespace-normal">
              <GraphCreationButton
                accountRole={accountRole}
                isCloudOnlyPlan={isCloudOnlyPlan}
                accountId={accountId}
              />
            </div>
          </div>
          <div className="col-span-2 h-full lg:col-span-1">
            <SupergraphsList
              shouldSeeSupergraphAccountView={shouldSeeSupergraphAccountView}
              accountId={accountId}
              supergraphListSearch={supergraphListSearch}
            />
          </div>
        </div>

        {/* Additional resources section - hidden on small screens, 1/4 width on md+ */}
        <div className="col-span-1 row-span-2 hidden md:block">
          <Heading className="my-5" size="lg">
            Additional resources
          </Heading>
          <Card className="mb-3">
            <CardHeader className="pb-0">
              <IconCommunity className="self-start" />
            </CardHeader>
            <CardBody className="whitespace-normal break-words">
              Explore advanced use cases, get answers fast, and learn from other
              developers, architects, and API leaders.
              <Link
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                to={CommunityRouter.path('Home')}
                className="block whitespace-normal break-words"
              >
                Join the Community
              </Link>
            </CardBody>
          </Card>
          <Card className="mb-3">
            <CardHeader className="pb-0">
              <IconEducation className="self-start" />
            </CardHeader>
            <CardBody className="whitespace-normal break-words">
              Expand your GraphQL knowledge with interactive, hands-on tutorials
              from our Education team.
              <Link
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                to={OdysseyRouter.path('Home')}
                className="block whitespace-normal break-words"
              >
                Learn with Apollo Odyssey
              </Link>
            </CardBody>
          </Card>
          <Card>
            <CardHeader className="pb-0">
              <IconDocument className="self-start" />
            </CardHeader>
            <CardBody className="whitespace-normal break-words">
              Dive into all things GraphQL and Apollo with our documentation.
              <Link
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                to={DocsRouter.path('StudioHome')}
                className="block whitespace-normal break-words"
              >
                Explore our docs
              </Link>
            </CardBody>
          </Card>
        </div>
      </div>
      {selectedGraphId && selectedGraphVariant && (
        <LazyUrlModal name={Config.modals.buildErrors}>
          {({ isOpen, closeModal }) => (
            <BuildErrorModal
              isOpen={isOpen}
              onClose={() =>
                closeModal(undefined, [
                  Config.queryParameters.GraphId,
                  Config.queryParameters.SelectedVariant,
                ])
              }
              graphRef={{
                graphId: selectedGraphId,
                graphVariant: selectedGraphVariant,
              }}
            />
          )}
        </LazyUrlModal>
      )}
      {initiatingGraphRef && (
        <LazyUrlModal name={Config.modals.initiatingCloudEndpoint}>
          {({ isOpen }) =>
            isOpen ? (
              // this modal has effects that we only want to happen when its shown, don't render when not open
              <InitiatingNewSupergraphEndpointModal
                graphRef={initiatingGraphRef}
                onClose={() =>
                  closeInitiatingNewSupergraphModal(initiatingGraphRef)
                }
              />
            ) : null
          }
        </LazyUrlModal>
      )}
    </>
  );
};
